import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useMemo } from "react";
import { tableFeatures, useTable } from "@tanstack/react-table";

// v9 makes every optional feature opt-in. Sorting, filtering and paging all run
// on the server for these lists, so the always-present core row model is enough
// and none of that feature code reaches the bundle.
const features = tableFeatures({});

/**
 * Server-paginated table built on TanStack Table and MUI's table primitives.
 * Columns are TanStack column defs; `meta.minWidth` and `meta.align` are read
 * here to size and align cells.
 *
 * Selection is a plain array of row ids rather than TanStack's row-selection
 * feature, because every caller already stores and consumes it that way.
 */
export const DataTable = ({
  columns,
  rows,
  getRowId,
  rowCount,
  page,
  pageSize,
  pageSizeOptions = [25],
  onPaginationChange,
  onRowClick,
  getRowClassName,
  columnVisibility,
  selectable = false,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  selectOnRowClick = false,
  loading = false,
  emptyMessage = "No rows",
  sx,
  "aria-label": ariaLabel,
}) => {
  const visibleColumns = useMemo(
    () =>
      columnVisibility
        ? columns.filter(
            (column) =>
              columnVisibility[column.id ?? column.accessorKey] !== false,
          )
        : columns,
    [columns, columnVisibility],
  );

  const table = useTable({
    features,
    columns: visibleColumns,
    data: rows,
    getRowId,
  });
  const headerGroups = table.getHeaderGroups();
  const bodyRows = table.getRowModel().rows;
  const columnCount = visibleColumns.length + (selectable ? 1 : 0);

  const pageIds = bodyRows
    .filter((row) => isRowSelectable?.(row.original) ?? true)
    .map((row) => row.id);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds?.includes(id));
  const someSelected =
    pageIds.some((id) => selectedIds?.includes(id)) && !allSelected;

  const toggleAll = () =>
    onSelectionChange(
      allSelected
        ? selectedIds.filter((id) => !pageIds.includes(id))
        : [...new Set([...selectedIds, ...pageIds])],
    );

  const toggleRow = (id) =>
    onSelectionChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selected) => selected !== id)
        : [...selectedIds, id],
    );

  const handleRowClick = (row) => {
    if (selectOnRowClick && (isRowSelectable?.(row.original) ?? true)) {
      toggleRow(row.id);
      return;
    }

    onRowClick?.(row.original);
  };

  const rowsAreClickable = Boolean(onRowClick || selectOnRowClick);

  // A URL can name a page that does not exist, either because it was typed or
  // because a filter has since narrowed the results, so clamp to what we have.
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(rowCount / pageSize) - 1),
  );

  return (
    <Paper variant="outlined" sx={{ width: "100%" }}>
      {/* Reserve the bar's height even when idle so rows never jump on load. */}
      <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
      <TableContainer>
        <Table size="small" aria-label={ariaLabel} sx={sx}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      slotProps={{ input: { "aria-label": "Select all rows" } }}
                    />
                  </TableCell>
                )}
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    align={header.column.columnDef.meta?.align}
                    sx={{
                      fontWeight: 600,
                      minWidth: header.column.columnDef.meta?.minWidth,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <table.FlexRender header={header} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {bodyRows.map((row) => (
              <TableRow
                key={row.id}
                hover={rowsAreClickable}
                selected={selectable && selectedIds.includes(row.id)}
                className={getRowClassName?.(row.original)}
                onClick={() => handleRowClick(row)}
                sx={{ cursor: rowsAreClickable ? "pointer" : "default" }}
              >
                {selectable && (
                  <TableCell padding="checkbox" onClick={stopPropagation}>
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      disabled={!(isRowSelectable?.(row.original) ?? true)}
                      onChange={() => toggleRow(row.id)}
                      slotProps={{
                        input: { "aria-label": `Select row ${row.id}` },
                      }}
                    />
                  </TableCell>
                )}
                {row.getAllCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    align={cell.column.columnDef.meta?.align}
                  >
                    <table.FlexRender cell={cell} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {bodyRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rowCount}
        page={currentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={pageSizeOptions}
        showFirstButton
        showLastButton
        onPageChange={(event, nextPage) =>
          onPaginationChange({ page: nextPage, pageSize })
        }
        onRowsPerPageChange={(event) =>
          onPaginationChange({ page: 0, pageSize: Number(event.target.value) })
        }
      />
    </Paper>
  );
};

const stopPropagation = (event) => event.stopPropagation();
